import { type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, useId } from "react";
import { cx } from "./cx.js";

export interface FieldControlProps {
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
  required: boolean | undefined;
}

/** The look of every text-like control. Exported so a bespoke control (a textarea, a masked PIN) matches. */
export const CONTROL =
  "block w-full min-h-9 rounded-md border border-line-strong bg-surface px-3 py-1.5 text-sm text-ink placeholder:text-ink-muted focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-1 focus-visible:outline-brand disabled:bg-subtle disabled:text-ink-muted";

/**
 * A control with its visible label, optional helper text, and the error below it. The control is a render prop so the
 * label, helper and error are wired by id (for, aria-describedby, aria-invalid) without the page doing it.
 */
export function Field({
  label,
  helper,
  error,
  required,
  children,
}: {
  label: string;
  helper?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  children: (p: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const note = error ? `${id}-err` : helper ? `${id}-help` : undefined;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </label>
      {children({
        id,
        "aria-describedby": note,
        "aria-invalid": error ? true : undefined,
        required: required || undefined,
      })}
      {error ? (
        <p id={`${id}-err`} role="alert" className="m-0 text-xs font-medium text-alarm">
          {error}
        </p>
      ) : helper ? (
        <p id={`${id}-help`} className="m-0 text-xs text-ink-muted">
          {helper}
        </p>
      ) : null}
    </div>
  );
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cx(CONTROL, className)} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx(CONTROL, className)}>
      {children}
    </select>
  );
}
